// Copyright (c) 2022-2025 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import './Tasks.css';
import { withStyles } from "tss-react/mui";
import { Dialog, Grid, DialogContent, DialogActions } from '@mui/material';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import DialogTitle from '@shared/components/DialogTitle';
import { NodeCheckEventArgs, NodeExpandEventArgs, TreeViewComponent } from '@syncfusion/ej2-react-navigations';
import container from "@core/di";
import DeliverablesStore from "@shared/stores/deliverables";
import Loading from '../Loading';
import { OrderTask, TaskNode } from '@shared/models/ordertask';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { Check4CheckAll, findStageNode, formatFilterTaskTree, FormatWorkFlows, getAllLeafNodes, mkUserKey, setEffectedNodes } from '@shared/utils/common';
import { LABEL_CLEAR_ALL, LABEL_SELECT_ALL } from '@shared/constants/filters';
import { TaskTreeItemDTO } from '@shared/models/deliverable/task-tree-item';
import AuthStore from '@core/stores/auth';

// Props interface for the main class.
export interface TasksProps {
  className?: string;
  classes?: Record<string, string>;
  orderTasks?: Array<OrderTask>;  // if provided, uncheck stages not in this list.
  forFiltering?: boolean;         // wheter the dialog is used for filtering.
  onClose: () => any;
  onSubmit: (selectedDelis: OrderTask[]) => any;
}

// State interface for the main class.
export interface TaskState {
  loading: boolean;
  treeItems: Array<TaskTreeItemDTO>; // Unformated tree nodes.
  taskData: Array<Array<TaskNode>>; // datasource for the task tree.
}

// Class to display the Workflow tree form for users to configure the tasks for an order.
// This is also used in the WorkFlow filter.  For filtering, a check/uncheck-all checkbox is prepend to the tree items.
// Note: there are problems with duplicate leaf/task nodes between treeObjs.  The check/uncheck-all checkbox is not working properly.
// To work around this, use a separate treeObj for each workflow.
class Tasks extends React.Component<TasksProps, TaskState> {
  deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  authStore = container.get<AuthStore>(AuthStore.diToken);
  treeObjs: Array<TreeViewComponent | null> = [];   // ref to the workflow-stage-task treeComponent.
  taskCheckbox: CheckBoxComponent | null;           // ref to the check/clear-all checkboxComponent prepend to the task trees.
  private selectedDelis: OrderTask[];               // list of tasks selected from the workflow trees.
  private storageKey: string;
  scrollableRef: React.RefObject<HTMLDivElement>;

  constructor(props: TasksProps) {
    super(props);
    const { user } = this.authStore;
    this.storageKey = mkUserKey(user.id, 'taskWorkflow');

    this.scrollableRef = React.createRef();

    this.state = {
      loading: true,
      treeItems: [],
      taskData: [], // datasources for the task trees.
    };
  }

  // Load the task tree data from the web server when the component is mounted.
  async componentDidMount(): Promise<void> {
    const { orderTasks } = this.props;    
    await this.loadTaskTree(orderTasks);

    // Restore the scroll position on component mount
    const savedScrollPosition = localStorage.getItem("gridScrollPosition");
    console.log("********** restore Scroll Position1: ", savedScrollPosition, this.scrollableRef);

    if (savedScrollPosition && this.scrollableRef.current) {
      console.log("********** restore Scroll Position2: ", savedScrollPosition);
      this.scrollableRef.current.scrollTop = parseInt(savedScrollPosition, 10);
    }

    // Add event listener to save the scroll position
    window.addEventListener("beforeunload", this.saveScrollPosition);    
  }

  componentDidUpdate(prevProps: TasksProps) {
    // Restore the scroll position on component mount
    const savedScrollPosition = localStorage.getItem("gridScrollPosition");
    console.log("********** restore Scroll Position3: ", savedScrollPosition, this.scrollableRef);

    if (savedScrollPosition && this.scrollableRef.current) {
      console.log("********** restore Scroll Position4: ", savedScrollPosition);
      this.scrollableRef.current.scrollTop = parseInt(savedScrollPosition, 10);
    }
  }

  componentWillUnmount() {
    // Remove event listener when the component unmounts
    window.removeEventListener("beforeunload", this.saveScrollPosition);
  }

  saveScrollPosition = () => {
    console.log("********************* HERE: saveScrollPosition:", this.scrollableRef);
    // Save the scroll position to localStorage
    if (this.scrollableRef.current) {
        console.log("********** Saving Scroll Position: ", this.scrollableRef.current.scrollTop);
      localStorage.setItem("gridScrollPosition", (this.scrollableRef.current.scrollTop).toString());
    }
  };
  
  // Load the task tree data from the web server,
  // Then build the data structures to display in the tasktreeComponent.
  async loadTaskTree(orderTasks: Array<OrderTask> | undefined) {
    const { forFiltering } = this.props;
    let treeItems: Array<TaskTreeItemDTO>;
    let taskData: Array<Array<TaskNode>> = [];

    if(forFiltering && forFiltering == true) {  // For filtering, fetch the stage-task tree.
      treeItems = await this.deliverablesStore.getTaskTree();
      if (treeItems) {
        taskData[0] = formatFilterTaskTree(treeItems, orderTasks, forFiltering);
      }
    }
    else if (orderTasks && orderTasks.length > 0) {  // For editing, fetch the workflow-stage-task tree.
      treeItems = await this.deliverablesStore.getTaskTree(2);
      console.log("******************************* treeItems:", treeItems);
      if (treeItems) {
        taskData = FormatWorkFlows(treeItems, orderTasks);

        //console.log("*** Task data:", taskData);
        //console.log("*** Order tasks:", orderTasks);
        // Find the workflow with the most selected items.
        // Note: "All" must be last in the list.
        let maxCnt = -1;
        let maxIdx = -1;
        for(let i = 0; i < taskData.length - 1; i++) { // Skip the last item "All".
          let curCnt = 0;
          taskData[i].forEach(item => {
            if(item.checked && (item.hasChildren == undefined || item.hasChildren == false)) {
              curCnt++;
            }
          });
          if(curCnt > maxCnt) {
            maxCnt = curCnt;
            maxIdx = i;
          }
        }
        console.log("*** Max checked count:", taskData[maxIdx][0].text, maxCnt); // use this to highlight the most selected workflow.
        // console.log("*** Task data:", taskData);
      }
    }
    else { // For new order, fetch the workflow-stage-task tree with first workflow selected.
      treeItems = await this.deliverablesStore.getTaskTree(1);
      if (treeItems) {
        taskData = FormatWorkFlows(treeItems);
      }
    }

    // Trigger a rerender after loading tree data.
    this.setState({ loading: false, treeItems: treeItems, taskData: taskData });    
  }

  // Load the task tree data from the web server,
  // Then build the data structures to display in the tasktreeComponent.
  async loadTaskTree_org(orderTasks: Array<OrderTask> | undefined) {
    const { forFiltering } = this.props;
    let treeItems: Array<TaskTreeItemDTO>;
    let taskData: Array<Array<TaskNode>> = [];

    if(forFiltering && forFiltering == true) {  // For filtering, fetch the stage-task tree.
      treeItems = await this.deliverablesStore.getTaskTree();
      if (treeItems) {
        taskData[0] = formatFilterTaskTree(treeItems, orderTasks, forFiltering);
      }
    }
    else if (orderTasks && orderTasks.length > 0) {  // For editing, fetch the workflow-stage-task tree.
      treeItems = await this.deliverablesStore.getTaskTree(2);
      console.log("******************************* treeItems:", treeItems);
      if (treeItems) {
        taskData = FormatWorkFlows(treeItems, orderTasks);

        //console.log("*** Task data:", taskData);
        //console.log("*** Order tasks:", orderTasks);
        // Find the workflow with the most selected items.
        // Note: "All" must be last in the list.
        let maxCnt = -1;
        let maxIdx = -1;
        for(let i = 0; i < taskData.length - 1; i++) { // Skip the last item "All".
          let curCnt = 0;
          taskData[i].forEach(item => {
            if(item.checked && (item.hasChildren == undefined || item.hasChildren == false)) {
              curCnt++;
            }
          });
          if(curCnt > maxCnt) {
            maxCnt = curCnt;
            maxIdx = i;
          }
        }
        console.log("*** Max checked count:", taskData[maxIdx][0].text, maxCnt); // use this to highlight the most selected workflow.
        // console.log("*** Task data:", taskData);
      }
    }
    else { // For new order, fetch the workflow-stage-task tree with first workflow selected.
      treeItems = await this.deliverablesStore.getTaskTree(1);
      if (treeItems) {
        taskData = FormatWorkFlows(treeItems);
      }
    }

    // Trigger a rerender after loading tree data.
    this.setState({ loading: false, treeItems: treeItems, taskData: taskData });    
  }

  // Check/unCheck all nodes in the filter treeView
  // Note: this event applies to the filter dialog only.  The treeObjs should have only one item.
  onCheckAllChange = (args: any) => {

    if(this.treeObjs.length > 0 && this.treeObjs[0] && this.taskCheckbox) {
      if (args.checked) {
        this.treeObjs[0].checkAll();
        this.taskCheckbox.label = LABEL_CLEAR_ALL;
      } 
      else {
        this.treeObjs[0].uncheckAll();
        this.taskCheckbox.label = LABEL_SELECT_ALL;
      }
    }
  }
  
  // When a node is checked/unchecked, find and reset the checked status of the check/uncheck-all checkbox.
  // Note: this event applies to the filter dialog only.  The treeObjs should have only one item.
  onTaskNodeChecked = (args: NodeCheckEventArgs) => {
    const { forFiltering } = this.props;
    console.log("********************************Task node checked event:", forFiltering, args);

    // For the filter dialog, check/uncheck all nodes when clicked.
    if(forFiltering) {
      if(this.treeObjs.length > 0 && this.treeObjs[0] != undefined && this.taskCheckbox != undefined) {
        this.taskCheckbox.checked  = this.treeObjs[0].getAllCheckedNodes().length == this.treeObjs[0].getTreeData().length; // number of checks == number of nodes
        this.taskCheckbox.label = this.taskCheckbox.checked ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL;
      }
    }
    // For editing order details, check/uncheck the same item in all treeObjs.
    // The 3-level tree structure has this format:
    //   - Workflows with ID 900000 + n where n is the 1-based index of the workflow.
    //     - Stages with ID 10000 * n + m where m is the 1-based index of the stage. 
    //       - Tasks with ID less than 10000 and shared between workflows.
    else { 
      console.log("******************************** Check taskData:", this.state.taskData);
      const { data, action } = args;
      const { treeItems, taskData } = this.state;
      const affectedIds: number[] = [];
      if(data && data.length > 0) {
        const id = Number(data[0].id);
        if(id < 10000) {// this is a task node.
          affectedIds.push(id);
        }
        else if(id < 900000) { // this is a stage node.
          const stageNode = findStageNode(id, treeItems);
          if(stageNode) {
            const taskNodes = getAllLeafNodes(stageNode).map(x => x.id);
            if(taskNodes && taskNodes.length > 0) {
              affectedIds.push(...taskNodes);
            }
          }
        }
        else if(id > 900000) { // This is a workflow node.
          const workflowIdx = id - 900000 - 1;
          const taskNodes = getAllLeafNodes(treeItems[workflowIdx]).map(x => x.id);
          if(taskNodes && taskNodes.length > 0) {
            affectedIds.push(...taskNodes);
          }
        }

        console.log("******************************** Affected task nodes:", affectedIds);
        if(affectedIds.length > 0) {
          const newTaskData = setEffectedNodes(taskData, affectedIds, action == 'check');

          this.setState({taskData: newTaskData});
          
          setTimeout(() => {
            const storedItems = localStorage.getItem(this.storageKey);
            const expandedNodeIds: Array<string> = storedItems ? JSON.parse(storedItems) : [];
        
            this.treeObjs.forEach((treeObj) => {
              if(treeObj) {
                treeObj.expandedNodes = expandedNodeIds;
                treeObj.refresh();
              }
            });
          }, 100);
        }
      }
    }
  }

  onTaskNodeClicked = (args: NodeExpandEventArgs) => {
    const { nodeData } = args; 

    const storedItems = localStorage.getItem(this.storageKey);
    const expandedNodeIds: Array<string> = storedItems ? JSON.parse(storedItems) : [];
    console.log("******* load expanded node from local storage:", expandedNodeIds)

    console.log("******************* onTaskNodeClicked:", args);

    // Add/remove the node id from the current list.
    if(nodeData && nodeData.expanded != undefined && nodeData.expanded != null) {
      const idStr = nodeData.id.toString().trim();
      if(idStr && idStr != "") {
        if(nodeData.expanded && idStr
          && expandedNodeIds.indexOf(idStr) < 0) {
          console.log("******************* Add:", idStr);
          expandedNodeIds.push(idStr);
        }
        else {
          const idx = expandedNodeIds.indexOf(idStr);
          if(idx >= 0) {
            console.log("******************* Remove:", idStr);
            expandedNodeIds.splice(idx, 1);
          }
        }
      
        console.log("******************* Store expanded node ids:", expandedNodeIds);
        localStorage.setItem(this.storageKey, JSON.stringify(expandedNodeIds));
      }
    }
  }

  // Save selections to local variable.
  // Pass to parent for sending to web server
  handleSubmit = () => {

    console.log("********************************** inOK.", this.scrollableRef);

    const { taskData } = this.state;
    this.selectedDelis = [];
    if (this.treeObjs.length > 0) {
      // Iterate through the treeObjs.
      for(let i = 0; i < this.treeObjs.length; i++) {
        // Get all checked nodes from the current treeObj.  Note: there may be duplicated leaf/task nodes between treeObjs.
        const checkedIds = this.treeObjs[i]?.getAllCheckedNodes();
        if(checkedIds) {
          // Iterate through the checked nodes.
          checkedIds.forEach(id => {
            // Iterate through the taskData to find the leaf/task node with the same Id.
            taskData.forEach(data => {
              const item = data.find(x => x.id.toString() == id);
              if (item && (item.hasChildren == undefined || item.hasChildren == false) && // Get the leaf/task nodes only.
                  this.selectedDelis.some(x => x.delTypeId == item.id) == false) { // Avoid duplicates.
                this.selectedDelis.push({delTypeId: Number(item.id), isInternal: false});
              }
            });
          });
        }
      }
    }

    this.props.onSubmit(this.selectedDelis);
  }

// HTML form contents (the workflow tree view).
  private get content() {
    const { taskData } = this.state;
    const { forFiltering } = this.props;
    const classes = withStyles.getClasses(this.props);
    const isCheckedAll = Check4CheckAll(taskData[0]);
    const label = isCheckedAll ? LABEL_CLEAR_ALL : LABEL_SELECT_ALL;
    
    const storedItems = localStorage.getItem(this.storageKey);
    const expandedNodeIds: Array<string> = storedItems ? JSON.parse(storedItems) : [];

    console.log("********************************** Task tree content (taskData):", taskData, expandedNodeIds);

    return (
      <div className={classes.treeControlWrapper}
        ref={this.scrollableRef}
        onScroll={this.saveScrollPosition.bind(this)}
      >
        {forFiltering && (  // Add a check/uncheck-all checkbox when used for filtering.
        <Grid container>
          <Grid item style={{ marginLeft: 54, marginBottom: 5 }}>
            <CheckBoxComponent
              ref =  {(scope) => {this.taskCheckbox = scope}}
              id="checkAll"
              checked={isCheckedAll}
              label={label}
              change={this.onCheckAllChange.bind(this)}
            />
          </Grid>
        </Grid>)
        // Add a treeViewComponent for each workflow.  Cannot combine to one tree due to workflows containing the same tasks.
        }
        <Grid container direction="row">
          <Grid item>
            {taskData.length > 0 && 
              taskData.map((data, index) => {
                return (
                  <TreeViewComponent 
                    key={index}
                    id={`sfTreeViewTasks${index}`}
                    ref = {(scope) => {this.treeObjs[index] = scope}}

                    // Basic configs.
                    fields={{dataSource: data, isChecked: 'checked'}}
                    showCheckBox={true}
                    autoCheck={true}  // same result when commented out
                    //enablePersistence={true}

                    expandedNodes={expandedNodeIds}
                    // Event handlers.
                    nodeChecked={this.onTaskNodeChecked.bind(this)}

                    nodeExpanded={this.onTaskNodeClicked.bind(this)}
                    nodeCollapsed={this.onTaskNodeClicked.bind(this)}
                  />
                );
              })
            }
          </Grid>
        </Grid>
      </div>
    );
  }

  // Render the Tasks Template dialog.
  render() {
    const { onClose, forFiltering } = this.props;
    const classes = withStyles.getClasses(this.props);

    if(this.state.loading){
      return (<Loading absolute/>);
    }

    return (
      <Dialog
        open
        classes={{ paper: classes.root }}
      >
        <DialogTitle
          classes={{ root: classes.title }}
          heading={(
            <Flex alignItems="center">
              <h1 className={classes.heading}>{ forFiltering ? 'Filter by workflow' : 'Tasks template'}</h1>
            </Flex>
          )}
          onClose={onClose}
        />

        <DialogContent className={classes.content}>
          {this.content}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <form onSubmit={this.handleSubmit.bind(this)} className={classes.form}>
            <Flex
              autoWidth={false}
              alignItems="center"
              wrap="nowrap"
            >
              <Grid container justifyContent={ forFiltering ? "flex-start" : "center"}>
                <Button
                  className={classes.actionBtn}
                  type="submit"
                  text={ forFiltering ? "Filter" : "OK"}
                />
              </Grid>
            </Flex>
          </form>
        </DialogActions>
      </Dialog >
    );
  }  
}


const TasksStyled = withStyles(
  Tasks, 
  (theme, props) => ({
    "root": {
      width: 500,
      maxWidth: 'none',
      margin: 0,
      border: 'none',
    },
    "title": {
      borderBottom: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "heading": {
      fontSize: 20,
      fontWeight: 500,
    },
    "closeIcon": {
      padding: 0,
    },
    "content": {
      padding: theme.spacing(0, 6),
      fontSize: 15,
      minHeight: 235,
    },
    "actions": {
      backgroundColor: theme.palette.colors.greyLight5,
      padding: theme.spacing(6),
      borderTop: `1px solid ${theme.palette.colors.greyLight}`,
    },
    "actionBtn": {
      fontSize: 16,
      marginLeft: theme.spacing(4),
    },
    "form": {
      width: '100%',
    },
    "treeControlWrapper": {
        height: '300px',
        overflow: 'auto',
        margin: '5px 5px',
        border: '1px solid #dddddd',
        borderRadius: '3px',
    }
  })
);

export default TasksStyled;